import { ApolloError, isApolloError } from "@apollo/client/errors";
import { useAlert } from "@hl/base-components/lib/alert/useAlert";
import { logError } from "@hl/shared-features/lib/services/logger";

import { useGetPageUploadUrlMutation } from "~/features/pages/Editor/editor.graphql.generated";
import { EditorNodes } from "~/features/pages/Editor/logic";
import { PageSection } from "~/features/pages/ManagePage/state";

export const usePageUpload = () => {
  const [getPageUploadUrl] = useGetPageUploadUrlMutation();
  const alert = useAlert();

  const getPageUploadUrlData = async (node: EditorNodes) => {
    try {
      return (
        await getPageUploadUrl({
          variables: {
            data: {
              mime: node.mediaMime,
              type: node.mediaType,
            },
          },
        })
      ).data;
    } catch (e) {
      await logError({
        e,
        message: "[PublishModal] failed to generate upload URL",
      });
      if (isApolloError(e as Error)) {
        const apolloError = e as ApolloError;
        const TOO_MANY_REQUESTS = 429;
        if (
          apolloError.graphQLErrors[0]?.extensions.status === TOO_MANY_REQUESTS
        ) {
          alert.error(apolloError.message);
          return;
        }
      }
      alert.error("An unexpected error occurred, please try again.");
    }
  };

  return {
    getPageUploadUrlData,
  };
};

/**
 * Return a simple dictionary lookup of which content types (text, image, etc.)
 * exist in the editor's state.
 *
 * @param editorState active editor state
 */
export const editorStateContainsTypes = async (
  pageSections: PageSection[]
): Promise<Record<string, boolean>> => {
  try {
    const contentTypes: Record<string, boolean> = {
      hasText: false,
      hasHtml: false,
      hasVideo: false,
      hasImage: false,
      hasAudio: false,
      hasEmbed: false,
    };

    const setters: Record<string, string> = {
      paragraph: "hasText",
      image: "hasImage",
      audio: "hasAudio",
      video: "hasVideo",
      embed: "hasEmbed",
      htmltag: "hasHtml",
    };
    for (const section of pageSections) {
      const editorData = JSON.parse(
        section.content.editorContent ? section.content.editorContent.state : ""
      );
      if (
        editorData.root.children.length === 1 &&
        editorData.root.children[0].children.length === 0
      ) {
        continue;
      }

      for (const key in editorData.root.children) {
        const node: EditorNodes = editorData.root.children[key];

        if (Object.keys(setters).includes(node?.type)) {
          contentTypes[setters[node?.type]] = true;
        }
      }
    }

    return contentTypes;
  } catch (error) {
    logError({
      error,
      message:
        "Was not able to determine what type of content the gated page has",
    });
    return {};
  }
};
